import { Suspense, lazy, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useChatWidget } from '@/context/ChatWidgetContext';

/**
 * Lazy boundary for the chat widget.
 *
 * The overlay pulls in the whole chat stack — react-markdown + remark-gfm,
 * react-syntax-highlighter, recharts (AstroChart) and fingerprintjs. Because
 * <ChatWidgetOverlay/> is mounted at the root of every route, all of that used
 * to land in the initial `app` chunk: 1.5 MB of JS parsed on every page load,
 * including the static SEO landing pages that can never open a chart. That was
 * the single 2.5 s long task behind the poor INP / performance scores.
 *
 * Splitting it here keeps the initial payload to the shell and defers the chat
 * code until the widget is actually opened (or the browser goes idle).
 */
const loadOverlay = () => import('./ChatWidgetOverlayContent');

const ChatWidgetOverlayContent = lazy(() =>
  loadOverlay().then((m) => ({ default: m.ChatWidgetOverlayContent })),
);

/**
 * Start fetching the chat chunk on intent — hover, focus or pointer-down on the
 * FAB — so it is usually in flight before the click even lands. Repeat calls are
 * free: the module registry caches the promise.
 */
export function prefetchChatWidget() {
  void loadOverlay();
}

/**
 * Warm the chunk once the page has finished loading AND the main thread is free,
 * so opening the widget feels instant without the warm-up racing the page it is
 * meant to be invisible behind.
 *
 * The `load` gate is the point: with a bare idle callback the chat stack (~350 KB
 * across several chunks) was fetched at High priority ~1.2 s into the load, inside
 * the window that Total Blocking Time and Time to Interactive are measured over.
 * Deferring past `load` costs nothing perceptible — anyone actually reaching for
 * the widget triggers prefetchChatWidget() on hover/focus/pointer-down first.
 */
function usePrefetchOnIdle() {
  useEffect(() => {
    let idleHandle: number | undefined;
    let timer: number | undefined;

    const prefetch = () => void loadOverlay();
    const scheduleIdle = () => {
      const idle = window.requestIdleCallback;
      if (idle) idleHandle = idle(prefetch, { timeout: 4000 });
      else timer = window.setTimeout(prefetch, 2500);
    };

    if (document.readyState === 'complete') {
      scheduleIdle();
      return () => {
        if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
        if (timer !== undefined) window.clearTimeout(timer);
      };
    }

    window.addEventListener('load', scheduleIdle, { once: true });
    return () => {
      window.removeEventListener('load', scheduleIdle);
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);
}

/**
 * Full-screen placeholder shown only if the chunk hasn't arrived yet. It is
 * fixed-position and covers the viewport, so swapping it for the real overlay
 * costs no layout shift.
 */
function OverlaySkeleton() {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950" role="status" aria-label="Opening chat">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
    </div>
  );
}

/**
 * Close the widget on route changes.
 *
 * This lives on the always-mounted wrapper rather than inside the lazy content:
 * the content component now mounts at open time, so a mount-time effect there
 * would close the widget the instant it was opened.
 */
function useCloseOnNavigate(closeWidget: () => void) {
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);

  useEffect(() => {
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;
    closeWidget();
  }, [pathname, closeWidget]);
}

export function ChatWidgetOverlay() {
  const { isOpen, closeWidget } = useChatWidget();
  usePrefetchOnIdle();
  useCloseOnNavigate(closeWidget);

  if (!isOpen) return null;

  return (
    <Suspense fallback={<OverlaySkeleton />}>
      <ChatWidgetOverlayContent />
    </Suspense>
  );
}
